import React from 'react';

export type LibraryHeroStat = {
  key: string;
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  /** 'warn' tints the tile that needs attention (late, past due). */
  tone?: 'warn' | null;
};

type LibraryHeroProps = {
  /** Small caps line above the title. */
  eyebrow?: string;
  title: string;
  body: React.ReactNode;
  stats: ReadonlyArray<LibraryHeroStat>;
  actions: React.ReactNode;
  /** Names the stat tiles for assistive tech, e.g. "Transfer summary". */
  statsLabel: string;
  /** Page-specific classes on the header, its actions and its stats (placement only). */
  className?: string;
  actionsClassName?: string;
  statsClassName?: string;
};

/**
 * A library page hero: Events-style headline, one row of actions and flat stat tiles. On phones
 * `heroStatsToggleEnhancer` folds the tiles behind a "Stats" toggle. Shared by Warehouse and Inventory.
 */
export default function LibraryHero({
  eyebrow = 'Library',
  title,
  body,
  stats,
  actions,
  statsLabel,
  className,
  actionsClassName,
  statsClassName,
}: LibraryHeroProps) {
  return (
    <header className={['settings-hero', className, 'library-phone-hero'].filter(Boolean).join(' ')}>
      <div className="settings-hero__headline library-phone-hero__group">
        <span className="settings-hero__eyebrow">{eyebrow}</span>
        <div className="settings-hero__title-row">
          <h1 className="settings-card__title m0">{title}</h1>
        </div>
        <p className="settings-hero__body">{body}</p>
      </div>
      <div className={['settings-hero__actions', actionsClassName].filter(Boolean).join(' ')}>{actions}</div>
      <div
        className={['settings-hero__meta events-hero-stats', statsClassName].filter(Boolean).join(' ')}
        aria-label={statsLabel}
        data-stats-toggle-label="Stats"
      >
        {stats.map((stat) => (
          <div key={stat.key} className="events-hero-stat" data-tone={stat.tone ?? undefined}>
            <span className="events-hero-stat__label">{stat.label}</span>
            <span className="events-hero-stat__value">{stat.value}</span>
            {stat.hint ? <span className="events-hero-stat__hint">{stat.hint}</span> : null}
          </div>
        ))}
      </div>
    </header>
  );
}
